// ✅ FIXED: Removed non-existent ApartmentFullView type
// This component should be refactored to use actual API types or removed if not needed

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { ApiService } from '../core/api';

/**
 * ⚠️ WARNING: ApartmentFullView type does not exist in the API
 * This component needs to be refactored to use:
 * - ApartmentDetailsDto (for basic apartment data)
 * - Separate API calls for related data
 *
 * TODO: Either:
 * 1. Create proper backend endpoint that returns all required data
 * 2. Make multiple API calls to fetch related data
 * 3. Remove this component if no longer needed
 */

// Temporary interface until proper API endpoint exists
interface ApartmentFullView {
  id: number;
  name: string;
  description?: string;
  floor?: number;
  size: number;
  rooms?: number;
  status: string;
  currentPrice?: number;
  currentPricePerM?: number;
  priceHistory?: Array<{
    validFrom: string;
    price: number;
    pricePerM: number;
  }>;
  media?: {
    logo?: string;
    pictures?: Array<{
      name?: string;
      src?: string;
      order?: number;
    }>;
  };
  developer?: {
    id: number;
    name: string;
    media?: {
      logo?: string;
    };
  };
  investment?: {
    id: number;
    name: string;
    plannedCompletionDate?: string;
    media?: {
      logo?: string;
    };
  };
  additionalPurchase?: {
    parkingList?: Array<{
      name: string;
      mandatory: boolean;
      currentPrice: number;
      priceHistory: any[];
    }>;
    ancillarySpaceList?: Array<{
      name: string;
      mandatory: boolean;
      currentPricePerM: number;
      priceHistory: any[];
    }>;
  };
}

@Component({
  selector: 'app-public-apartment-view',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    TableModule,
    CardModule,
    TagModule,
    DividerModule,
  ],
  template: `
    <div class="public-apartment-view">
      <!-- Nagłówek -->
      <div class="page-header">
        <h1>Szczegóły apartamentu - widok publiczny</h1>
        <p class="warning-message">
          ⚠️ Ten komponent wymaga refaktoryzacji - używa nieistniejącego endpointu API
        </p>
      </div>

      <!-- Stan ładowania -->
      @if (loading) {
        <div class="loading-container">
          <p-progressSpinner />
          <p>Ładowanie szczegółów apartamentu...</p>
        </div>
      }

      <!-- Stan błędu -->
      @if (error && !loading) {
        <div class="error-container">
          <p class="error-message">{{ error }}</p>
        </div>
      }

      <!-- Zawartość apartamentu -->
      @if (apartment && !loading && !error) {
        <div class="content">
          <!-- Podstawowe dane apartamentu -->
          <p-card header="Informacje o apartamencie">
            <p-table
              [value]="getBasicInfoData()"
              [tableStyle]="{ 'min-width': '50rem' }"
              >
              <ng-template pTemplate="header">
                <tr>
                  <th>Właściwość</th>
                  <th>Wartość</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td class="property-name">{{ item.property }}</td>
                  <td class="property-value">{{ item.value }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
          <!-- Dane dewelopera -->
          <p-card header="Deweloper">
            <p-table
              [value]="getDeveloperData()"
              [tableStyle]="{ 'min-width': '50rem' }"
              >
              <ng-template pTemplate="header">
                <tr>
                  <th>Właściwość</th>
                  <th>Wartość</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td class="property-name">{{ item.property }}</td>
                  <td class="property-value">{{ item.value }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
          <!-- Dane inwestycji -->
          <p-card header="Inwestycja">
            <p-table
              [value]="getInvestmentData()"
              [tableStyle]="{ 'min-width': '50rem' }"
              >
              <ng-template pTemplate="header">
                <tr>
                  <th>Właściwość</th>
                  <th>Wartość</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr>
                  <td class="property-name">{{ item.property }}</td>
                  <td class="property-value">{{ item.value }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
          <!-- Historia cen -->
          @if (apartment.priceHistory && apartment.priceHistory.length > 0) {
            <p-card
              header="Historia cen"
              >
              <p-table
                [value]="apartment.priceHistory"
                [tableStyle]="{ 'min-width': '50rem' }"
                >
                <ng-template pTemplate="header">
                  <tr>
                    <th>Data obowiązywania</th>
                    <th>Cena całkowita</th>
                    <th>Cena za m²</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-priceItem>
                  <tr>
                    <td>{{ formatDate(priceItem.validFrom) }}</td>
                    <td class="price-cell">
                      {{ priceItem.price | currency : 'PLN' : 'symbol' : '1.0-0' }}
                    </td>
                    <td class="price-cell">
                      {{
                      priceItem.pricePerM | currency : 'PLN' : 'symbol' : '1.0-0'
                      }}
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
          }
          <!-- Media -->
          @if (apartment.media) {
            <p-card header="Media">
              <div class="media-section">
                <!-- Logo -->
                @if (apartment.media.logo) {
                  <div class="media-item">
                    <h4>Logo:</h4>
                    <img
                      [src]="apartment.media.logo"
                      alt="Logo apartamentu"
                      class="logo-image"
                      />
                  </div>
                }
                <!-- Zdjęcia -->
                @if (
                  apartment.media.pictures && apartment.media.pictures.length > 0
                  ) {
                  <div
                    class="media-item"
                    >
                    <h4>Zdjęcia ({{ apartment.media.pictures.length }}):</h4>
                    <p-table
                      [value]="getSortedPictures()"
                      [tableStyle]="{ 'min-width': '50rem' }"
                      >
                      <ng-template pTemplate="header">
                        <tr>
                          <th>Nazwa</th>
                          <th>Kolejność</th>
                          <th>Podgląd</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-picture>
                        <tr>
                          <td>{{ picture.name || 'Bez nazwy' }}</td>
                          <td>{{ picture.order }}</td>
                          <td>
                            <img
                              [src]="picture.src"
                              [alt]="picture.name"
                              class="thumbnail-image"
                              />
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                }
                <!-- Brak mediów -->
                @if (
                  !apartment.media.logo &&
                  (!apartment.media.pictures ||
                  apartment.media.pictures.length === 0)
                  ) {
                  <div
                    >
                    <p>Brak dostępnych mediów.</p>
                  </div>
                }
              </div>
            </p-card>
          }
          <!-- Dodatkowe zakupy -->
          @if (apartment.additionalPurchase) {
            <p-card header="Dodatkowe zakupy">
              <!-- Parkingi -->
              @if (
                apartment.additionalPurchase.parkingList &&
                apartment.additionalPurchase.parkingList.length > 0
                ) {
                <div
                  class="additional-section"
                  >
                  <h4>Dostępne parkingi:</h4>
                  <p-table
                    [value]="apartment.additionalPurchase.parkingList"
                    [tableStyle]="{ 'min-width': '50rem' }"
                    >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>Nazwa</th>
                        <th>Obowiązkowy</th>
                        <th>Aktualna cena</th>
                        <th>Historia cen</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-parking>
                      <tr>
                        <td>{{ parking.name }}</td>
                        <td>
                          <p-tag
                            [value]="parking.mandatory ? 'Tak' : 'Nie'"
                            [severity]="parking.mandatory ? 'warn' : 'success'"
                            >
                          </p-tag>
                        </td>
                        <td class="price-cell">
                          {{
                          parking.currentPrice
                          | currency : 'PLN' : 'symbol' : '1.0-0'
                          }}
                        </td>
                        <td>{{ parking.priceHistory.length }} wersji</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>
              }
              <!-- Komórki lokatorskie -->
              @if (
                apartment.additionalPurchase.ancillarySpaceList &&
                apartment.additionalPurchase.ancillarySpaceList.length > 0
                ) {
                <div
                  class="additional-section"
                  >
                  <h4>Dostępne komórki:</h4>
                  <p-table
                    [value]="apartment.additionalPurchase.ancillarySpaceList"
                    [tableStyle]="{ 'min-width': '50rem' }"
                    >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>Nazwa</th>
                        <th>Obowiązkowy</th>
                        <th>Aktualna cena za m²</th>
                        <th>Historia cen</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-ancillary>
                      <tr>
                        <td>{{ ancillary.name }}</td>
                        <td>
                          <p-tag
                            [value]="ancillary.mandatory ? 'Tak' : 'Nie'"
                            [severity]="ancillary.mandatory ? 'warn' : 'success'"
                            >
                          </p-tag>
                        </td>
                        <td class="price-cell">
                          {{
                          ancillary.currentPricePerM
                          | currency : 'PLN' : 'symbol' : '1.0-0'
                          }}
                        </td>
                        <td>{{ ancillary.priceHistory.length }} wersji</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>
              }
              <!-- Brak dodatkowych zakupów -->
              @if (
                (!apartment.additionalPurchase.parkingList ||
                apartment.additionalPurchase.parkingList.length === 0) &&
                (!apartment.additionalPurchase.ancillarySpaceList ||
                apartment.additionalPurchase.ancillarySpaceList.length === 0)
                ) {
                <div
                  >
                  <p>Brak dostępnych dodatkowych zakupów.</p>
                </div>
              }
            </p-card>
          }
        </div>
      }
    </div>
    `,
  styles: [
    `
      .public-apartment-view {
        padding: 2rem;
        max-width: 1200px;
        margin: 0 auto;
        background: #f8f9fa;
        min-height: 100vh;
      }

      /* Nagłówek */
      .page-header {
        background: white;
        padding: 2rem;
        margin-bottom: 2rem;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .page-header h1 {
        margin: 0 0 0.5rem 0;
        font-size: 1.75rem;
        font-weight: 600;
        color: #2c3e50;
      }

      .warning-message {
        margin: 0;
        padding: 0.75rem;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 4px;
        color: #856404;
        font-size: 0.875rem;
      }

      /* Stany ładowania i błędu */
      .loading-container,
      .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        text-align: center;
        gap: 1rem;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .error-message {
        color: #e74c3c;
        font-weight: 500;
        margin: 0;
      }

      /* Zawartość */
      .content {
        display: flex;
        flex-direction: column;
        gap: 2rem;
      }

      /* Tabele */
      .property-name {
        font-weight: 600;
        color: #34495e;
        width: 30%;
      }

      .property-value {
        color: #2c3e50;
        font-weight: 500;
      }

      .price-cell {
        font-weight: 700;
        color: #3498db;
        text-align: right;
      }

      /* Media */
      .media-section {
        display: flex;
        flex-direction: column;
        gap: 2rem;
      }

      .media-item h4 {
        margin: 0 0 1rem 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: #34495e;
      }

      .logo-image {
        max-width: 200px;
        max-height: 100px;
        object-fit: contain;
        border: 1px solid #e9ecef;
        border-radius: 4px;
        padding: 0.5rem;
        background: white;
      }

      .thumbnail-image {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid #e9ecef;
      }

      /* Dodatkowe sekcje */
      .additional-section {
        margin-bottom: 2rem;
      }

      .additional-section:last-child {
        margin-bottom: 0;
      }

      .additional-section h4 {
        margin: 0 0 1rem 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: #34495e;
      }

      /* Responsywność */
      @media (max-width: 768px) {
        .public-apartment-view {
          padding: 1rem;
        }

        .page-header {
          padding: 1.5rem;
        }

        .page-header h1 {
          font-size: 1.5rem;
        }

        .property-name {
          width: auto;
        }
      }
    `,
  ],
})
export class PublicApartmentViewComponent implements OnInit {
  apartment: ApartmentFullView | null = null;
  loading = false;
  error: string | null = null;

  constructor(private route: ActivatedRoute, private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadApartmentDetails();
  }

  loadApartmentDetails(): void {
    this.loading = true;
    this.error = 'Ten widok wymaga implementacji dedykowanego endpointu API. Metoda getPublicApartmentFullView nie istnieje w ApiService.';
    this.loading = false;

    // ⚠️ This endpoint doesn't exist - commented out
    /*
    const apartmentId = this.route.snapshot.paramMap.get('apartmentId');

    if (!apartmentId) {
      this.error = 'Nie znaleziono ID apartamentu w parametrach ścieżki';
      this.loading = false;
      return;
    }

    this.apiService.getPublicApartmentFullView(Number(apartmentId)).subscribe({
      next: (apartment: ApartmentFullView) => {
        this.apartment = apartment;
        this.loading = false;
      },
      error: (err) => {
        console.error('Błąd ładowania publicznego widoku apartamentu:', err);
        this.error =
          err?.userMessage || 'Nie udało się załadować szczegółów apartamentu.';
        this.loading = false;
      },
    });
    */
  }

  getBasicInfoData(): { property: string; value: string }[] {
    if (!this.apartment) return [];

    return [
      { property: 'ID apartamentu', value: this.apartment.id.toString() },
      { property: 'Nazwa', value: this.apartment.name },
      { property: 'Opis', value: this.apartment.description || 'Brak opisu' },
      {
        property: 'Piętro',
        value: this.apartment.floor?.toString() || 'Nie określono',
      },
      { property: 'Powierzchnia', value: `${this.apartment.size} m²` },
      {
        property: 'Liczba pokoi',
        value: this.apartment.rooms?.toString() || 'Nie określono',
      },
      { property: 'Status', value: this.getStatusText(this.apartment.status) },
      {
        property: 'Aktualna cena',
        value: this.apartment.currentPrice
          ? `${this.apartment.currentPrice.toLocaleString('pl-PL')} PLN`
          : 'Brak ceny',
      },
      {
        property: 'Cena za m²',
        value: this.apartment.currentPricePerM
          ? `${this.apartment.currentPricePerM.toLocaleString('pl-PL')} PLN`
          : 'Brak ceny',
      },
    ];
  }

  getDeveloperData(): { property: string; value: string }[] {
    if (!this.apartment?.developer) return [];

    return [
      {
        property: 'ID dewelopera',
        value: this.apartment.developer.id.toString(),
      },
      { property: 'Nazwa dewelopera', value: this.apartment.developer.name },
      {
        property: 'Logo dewelopera',
        value: this.apartment.developer.media?.logo ? 'Dostępne' : 'Brak',
      },
    ];
  }

  getInvestmentData(): { property: string; value: string }[] {
    if (!this.apartment?.investment) return [];

    return [
      {
        property: 'ID inwestycji',
        value: this.apartment.investment.id.toString(),
      },
      { property: 'Nazwa inwestycji', value: this.apartment.investment.name },
      {
        property: 'Planowana data zakończenia',
        value: this.formatDate(
          this.apartment.investment.plannedCompletionDate || ''
        ),
      },
      {
        property: 'Logo inwestycji',
        value: this.apartment.investment.media?.logo ? 'Dostępne' : 'Brak',
      },
    ];
  }

  getSortedPictures() {
    if (!this.apartment?.media?.pictures) return [];
    return [...this.apartment.media.pictures].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'Brak daty';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  getStatusText(status: string): string {
    switch (status.toLowerCase()) {
      case 'available':
        return 'Dostępny';
      case 'reserved':
        return 'Rezerwacja';
      case 'sold':
        return 'Sprzedany';
      default:
        return status;
    }
  }
}
